import React, { useEffect, useState, useCallback, useRef } from "react";
import API from "../../../services/api";

const REASONS = ["Dead", "Shifted", "Duplicate", "Other"];
const MAX_BULK = 500;
const GRADIENT = "linear-gradient(135deg, #1a3c5e, #2d6a4f)";

const REASON_BADGE = {
  Dead: "bg-dark-subtle text-dark",
  Shifted: "bg-info-subtle text-info",
  Duplicate: "bg-warning-subtle text-warning",
  Other: "bg-secondary-subtle text-secondary",
};

const genderLabel = (g) =>
  g === "M" ? "Male" : g === "F" ? "Female" : g || "—";
const fullName = (v) => `${v.firstname_en || ""} ${v.lastname_en || ""}`.trim();
const knName = (v) => `${v.firstname_v1 || ""} ${v.lastname_v1 || ""}`.trim();
const relName = (v) =>
  `${v.relation_firstname_en || ""} ${v.relation_lastname_en || ""}`.trim();

// ── Village autocomplete (controlled by parent) ──────────────
const VillageInput = ({
  villList,
  text,
  selected,
  onTextChange,
  onSelect,
  onClear,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const matches = text
    ? villList
        .filter((v) => v.name.toLowerCase().includes(text.toLowerCase()))
        .slice(0, 8)
    : [];

  return (
    <div className="position-relative" ref={ref}>
      <div className="input-group">
        <span className="input-group-text bg-white border-end-0">
          <i className="bi bi-search text-muted small"></i>
        </span>
        <input
          type="text"
          className="form-control border-start-0"
          placeholder="Type village..."
          value={text}
          onChange={(e) => {
            onTextChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        {selected && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClear}
          >
            <i className="bi bi-x"></i>
          </button>
        )}
      </div>
      {open && !selected && matches.length > 0 && (
        <div
          className="border rounded-3 shadow bg-white position-absolute w-100 mt-1"
          style={{ zIndex: 9999, maxHeight: "180px", overflowY: "auto" }}
        >
          {matches.map((v) => (
            <div
              key={v.villageid}
              className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
              style={{ cursor: "pointer" }}
              onMouseDown={() => {
                onSelect(v);
                setOpen(false);
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f0f4ff")
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
            >
              <i className="bi bi-geo-alt text-muted small"></i>
              <span className="small fw-medium">{v.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Pagination ───────────────────────────────────────────────
const Pager = ({ page, totalPages, total, onPage }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center py-3 px-4 border-top">
      <span className="text-muted small">
        Page {page} of {totalPages} · {total.toLocaleString("en-IN")} total
      </span>
      <ul className="pagination pagination-sm mb-0">
        <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPage(page - 1)}>
            <i className="bi bi-chevron-left"></i>
          </button>
        </li>
        {[...Array(Math.min(totalPages, 5))].map((_, i) => {
          const p = page <= 3 ? i + 1 : page - 2 + i;
          if (p > totalPages) return null;
          return (
            <li key={p} className={`page-item ${page === p ? "active" : ""}`}>
              <button className="page-link" onClick={() => onPage(p)}>
                {p}
              </button>
            </li>
          );
        })}
        <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPage(page + 1)}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </li>
      </ul>
    </div>
  );
};

const VoterCleanup = () => {
  const [tab, setTab] = useState("remove");

  // masters
  const [villList, setVillList] = useState([]);
  const [boothList, setBoothList] = useState([]);
  const [casteList, setCasteList] = useState([]);

  // shared filters
  const [villText, setVillText] = useState("");
  const [selVill, setSelVill] = useState(null);
  const [boothNo, setBoothNo] = useState("");
  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  // list data (Remove / Edit / Removed tabs)
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  // selection (voter_id -> voter)
  const [sel, setSel] = useState({});
  const [removeReason, setRemoveReason] = useState("Dead");
  const [confirmMode, setConfirmMode] = useState(null); // 'remove' | 'restore'
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState(null);

  // duplicates
  const [dupType, setDupType] = useState("epic");
  const [dupData, setDupData] = useState(null);
  const [dupPage, setDupPage] = useState(1);

  // edit
  const [editVoter, setEditVoter] = useState(null);
  const [editMobile, setEditMobile] = useState("");
  const [editCaste, setEditCaste] = useState("");
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  const selCount = Object.keys(sel).length;
  const casteMap = {};
  casteList.forEach((c) => {
    casteMap[c.casteid] = c.castename;
  });
  const filteredBooths = selVill
    ? boothList.filter((b) => b.villageid === selVill.villageid)
    : [];

  const showNotice = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 6000);
  };

  // ── Masters ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [v, b, c] = await Promise.all([
          API.get("/masters/villages"),
          API.get("/masters/booths"),
          API.get("/masters/castes"),
        ]);
        setVillList(v.data.data);
        setBoothList(b.data.data);
        setCasteList(c.data.data);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  // ── Fetch voter list (Remove / Edit / Removed) ─────────────
  const fetchRows = useCallback(
    async (pg = 1) => {
      setLoading(true);
      try {
        const res = await API.get("/voters/", {
          params: {
            page: pg,
            per_page: 20,
            status: tab === "removed" ? "Deleted" : "Active",
            villageid: selVill ? selVill.villageid : undefined,
            booth_no: boothNo || undefined,
            search: search.trim() || undefined,
            reason:
              tab === "removed" && reasonFilter ? reasonFilter : undefined,
          },
        });
        const list = res.data.data.voters || [];
        const meta = res.data.data.pagination;
        const tp = Math.max(meta.total_pages || 1, 1);
        if (list.length === 0 && pg > 1) {
          return fetchRows(Math.min(pg - 1, tp));
        }
        setRows(list);
        setTotal(meta.total || 0);
        setTotalPages(tp);
        setPage(pg);
        setSearched(true);
      } catch (err) {
        showNotice(
          "danger",
          err.response?.data?.message || "Could not load voters",
        );
      } finally {
        setLoading(false);
      }
    },
    [tab, selVill, boothNo, search, reasonFilter],
  );

  // ── Fetch duplicates ───────────────────────────────────────
  const fetchDups = useCallback(
    async (pg = 1, type = dupType) => {
      setLoading(true);
      try {
        const res = await API.get("/voters/duplicates", {
          params: {
            type,
            page: pg,
            per_page: 10,
            villageid: selVill ? selVill.villageid : undefined,
            booth_no: boothNo || undefined,
          },
        });
        const data = res.data.data;
        if ((data.groups || []).length === 0 && pg > 1) {
          return fetchDups(
            Math.min(pg - 1, data.pagination.total_pages || 1),
            type,
          );
        }
        setDupData(data);
        setDupPage(pg);
      } catch (err) {
        showNotice(
          "danger",
          err.response?.data?.message || "Could not load duplicates",
        );
      } finally {
        setLoading(false);
      }
    },
    [dupType, selVill, boothNo],
  );

  // ── Tab change / reset: clear and auto-load where it makes sense ──
  useEffect(() => {
    setRows([]);
    setSel({});
    setTotal(0);
    setPage(1);
    setTotalPages(1);
    setSearched(false);
    setDupData(null);
    if (tab === "removed") fetchRows(1);
    if (tab === "duplicates") fetchDups(1);
    // eslint-disable-next-line
  }, [tab, reloadKey]);

  // ── Filter handlers ────────────────────────────────────────
  const onVillText = (val) => {
    setVillText(val);
    setSelVill(null);
    setBoothNo("");
  };
  const onVillSelect = (v) => {
    setVillText(v.name);
    setSelVill(v);
    setBoothNo("");
  };
  const onVillClear = () => {
    setVillText("");
    setSelVill(null);
    setBoothNo("");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSel({});
    if (tab === "duplicates") {
      fetchDups(1);
      return;
    }
    if (tab !== "removed" && !selVill && !search.trim()) {
      showNotice(
        "warning",
        "Select a village, or type a name / EPIC / mobile to search.",
      );
      return;
    }
    fetchRows(1);
  };

  const handleReset = () => {
    setVillText("");
    setSelVill(null);
    setBoothNo("");
    setSearch("");
    setReasonFilter("");
    setReloadKey((k) => k + 1);
  };

  // ── Selection ──────────────────────────────────────────────
  const toggleOne = (v) => {
    if (sel[v.voter_id]) {
      const n = { ...sel };
      delete n[v.voter_id];
      setSel(n);
      return;
    }
    if (selCount >= MAX_BULK) {
      showNotice(
        "warning",
        `You can select at most ${MAX_BULK} voters at a time.`,
      );
      return;
    }
    setSel({ ...sel, [v.voter_id]: v });
  };

  const togglePage = (list) => {
    const allSelected = list.length > 0 && list.every((v) => sel[v.voter_id]);
    const n = { ...sel };
    if (allSelected) {
      list.forEach((v) => delete n[v.voter_id]);
    } else {
      for (const v of list) {
        if (n[v.voter_id]) continue;
        if (Object.keys(n).length >= MAX_BULK) {
          showNotice("warning", `Selection limited to ${MAX_BULK} voters.`);
          break;
        }
        n[v.voter_id] = v;
      }
    }
    setSel(n);
  };

  const selectExtras = () => {
    const n = {};
    (dupData?.groups || []).forEach((g) =>
      g.voters.forEach((v) => {
        if (v.voter_id !== g.suggested_keep) n[v.voter_id] = v;
      }),
    );
    setSel(n);
  };

  // ── Remove / Restore ───────────────────────────────────────
  const reasonToUse = tab === "duplicates" ? "Duplicate" : removeReason;

  const fullGroups =
    tab === "duplicates"
      ? (dupData?.groups || []).filter((g) =>
          g.voters.every((v) => sel[v.voter_id]),
        ).length
      : 0;

  const doConfirm = async () => {
    const ids = Object.keys(sel).map(Number);
    if (!ids.length) return;
    setWorking(true);
    try {
      if (confirmMode === "remove") {
        const res = await API.post("/voters/bulk-remove", {
          voter_ids: ids,
          reason: reasonToUse,
        });
        showNotice("success", res.data.message || "Voters removed");
      } else {
        const res = await API.post("/voters/restore", { voter_ids: ids });
        showNotice("success", res.data.message || "Voters restored");
      }
      setConfirmMode(null);
      setSel({});
      if (tab === "duplicates") fetchDups(dupPage);
      else fetchRows(page);
    } catch (err) {
      showNotice("danger", err.response?.data?.message || "Action failed");
      setConfirmMode(null);
    } finally {
      setWorking(false);
    }
  };

  // ── Edit (mobile + caste only) ─────────────────────────────
  const openEdit = (v) => {
    setEditVoter(v);
    setEditMobile(v.mobile || "");
    setEditCaste(v.casteid ? String(v.casteid) : "");
    setEditError("");
  };

  const saveEdit = async () => {
    const mobile = editMobile.trim();
    if (mobile && !/^[0-9]{10}$/.test(mobile)) {
      setEditError("Mobile must be exactly 10 digits");
      return;
    }
    setSaving(true);
    try {
      const res = await API.put(`/voters/${editVoter.voter_id}`, {
        mobile,
        casteid: editCaste ? parseInt(editCaste, 10) : null,
      });
      const updated = res.data.data;
      setRows((prev) =>
        prev.map((r) => (r.voter_id === updated.voter_id ? updated : r)),
      );
      setEditVoter(null);
      showNotice("success", "Voter updated");
    } catch (err) {
      setEditError(err.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  // ── Action bar (shared) ────────────────────────────────────
  const renderActionBar = () => {
    if (selCount === 0) return null;
    return (
      <div
        className="d-flex flex-wrap align-items-center gap-2 px-4 py-3 border-bottom"
        style={{ background: "#f0f4ff" }}
      >
        <span className="badge bg-primary px-3 py-2">{selCount} selected</span>
        <button
          className="btn btn-sm btn-light border"
          onClick={() => setSel({})}
        >
          Clear
        </button>
        <div className="ms-auto d-flex align-items-center gap-2">
          {tab === "remove" && (
            <select
              className="form-select form-select-sm"
              style={{ width: "auto" }}
              value={removeReason}
              onChange={(e) => setRemoveReason(e.target.value)}
            >
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}
          {(tab === "remove" || tab === "duplicates") && (
            <button
              className="btn btn-sm btn-danger px-3"
              onClick={() => setConfirmMode("remove")}
            >
              <i className="bi bi-person-dash me-1"></i>
              Remove {selCount}
              {tab === "duplicates" ? " as Duplicate" : ""}
            </button>
          )}
          {tab === "removed" && (
            <button
              className="btn btn-sm btn-success px-3"
              onClick={() => setConfirmMode("restore")}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>Restore{" "}
              {selCount}
            </button>
          )}
        </div>
      </div>
    );
  };

  const tabs = [
    { key: "remove", icon: "bi-person-dash", label: "Remove Voters" },
    { key: "duplicates", icon: "bi-files", label: "Duplicates" },
    { key: "edit", icon: "bi-pencil-square", label: "Edit Mobile / Caste" },
    { key: "removed", icon: "bi-archive", label: "Removed List" },
  ];

  const listTab = tab !== "duplicates";
  const showCheck = tab === "remove" || tab === "removed";

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h4 className="fw-bold mb-1">Voter Cleanup</h4>
        <p className="text-muted small mb-0">
          Remove dead / shifted / duplicate voters, and update mobile or caste.
          Removed voters are hidden, not erased — you can restore them.
        </p>
      </div>

      {/* Notice */}
      {notice && (
        <div
          className={`alert alert-${notice.type} d-flex justify-content-between align-items-center py-2`}
        >
          <span>{notice.text}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setNotice(null)}
          ></button>
        </div>
      )}

      {/* Tabs */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-2 d-flex gap-2 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`btn rounded-3 px-4 ${tab === t.key ? "text-white" : "btn-light"}`}
              style={tab === t.key ? { background: GRADIENT } : {}}
              onClick={() => setTab(t.key)}
            >
              <i className={`bi ${t.icon} me-2`}></i>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <form onSubmit={handleSearch}>
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted mb-1">
                  Village
                </label>
                <VillageInput
                  villList={villList}
                  text={villText}
                  selected={selVill}
                  onTextChange={onVillText}
                  onSelect={onVillSelect}
                  onClear={onVillClear}
                />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">
                  Booth
                </label>
                <select
                  className="form-select"
                  value={boothNo}
                  onChange={(e) => setBoothNo(e.target.value)}
                  disabled={!selVill}
                >
                  <option value="">All Booths</option>
                  {filteredBooths.map((b) => (
                    <option key={b.boothid} value={b.booth_no}>
                      Booth {b.booth_no}
                    </option>
                  ))}
                </select>
              </div>
              {listTab && (
                <div className="col-12 col-md-3">
                  <label className="form-label small text-muted mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name, EPIC or mobile..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              )}
              {tab === "removed" && (
                <div className="col-6 col-md-2">
                  <label className="form-label small text-muted mb-1">
                    Reason
                  </label>
                  <select
                    className="form-select"
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value)}
                  >
                    <option value="">All reasons</option>
                    {REASONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-6 col-md-1">
                <button type="submit" className="btn btn-primary w-100">
                  <i className="bi bi-search"></i>
                </button>
              </div>
              <div className="col-6 col-md-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ── LIST TABS: Remove / Edit / Removed ── */}
      {listTab && (
        <div className="card border-0 shadow-sm rounded-4">
          {renderActionBar()}
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary"></div>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-people fs-1 d-block mb-2"></i>
                {tab === "removed"
                  ? "No removed voters found"
                  : searched
                    ? "No voters found for the selected filters"
                    : "Select a village or search to list voters"}
              </div>
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-hover align-middle mb-0"
                  style={{ fontSize: "13px" }}
                >
                  <thead className="bg-light">
                    <tr>
                      {showCheck && (
                        <th className="ps-4 py-3" style={{ width: 40 }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={
                              rows.length > 0 &&
                              rows.every((v) => sel[v.voter_id])
                            }
                            onChange={() => togglePage(rows)}
                          />
                        </th>
                      )}
                      <th className="py-3 small text-muted fw-semibold">#</th>
                      <th className="py-3 small text-muted fw-semibold">
                        EPIC No
                      </th>
                      <th className="py-3 small text-muted fw-semibold">
                        Name
                      </th>
                      <th className="py-3 small text-muted fw-semibold">
                        Gender / Age
                      </th>
                      <th className="py-3 small text-muted fw-semibold">
                        Booth
                      </th>
                      {tab !== "removed" && (
                        <th className="py-3 small text-muted fw-semibold">
                          Mobile
                        </th>
                      )}
                      {tab === "edit" && (
                        <th className="py-3 small text-muted fw-semibold">
                          Caste
                        </th>
                      )}
                      {tab === "removed" && (
                        <>
                          <th className="py-3 small text-muted fw-semibold">
                            Reason
                          </th>
                          <th className="py-3 small text-muted fw-semibold">
                            Removed On
                          </th>
                          <th className="py-3 small text-muted fw-semibold">
                            Removed By
                          </th>
                        </>
                      )}
                      {tab === "edit" && (
                        <th className="py-3 small text-muted fw-semibold text-end pe-4">
                          Action
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((v, idx) => (
                      <tr
                        key={v.voter_id}
                        className={sel[v.voter_id] ? "table-active" : ""}
                      >
                        {showCheck && (
                          <td className="ps-4">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={!!sel[v.voter_id]}
                              onChange={() => toggleOne(v)}
                            />
                          </td>
                        )}
                        <td className="text-muted small">
                          {(page - 1) * 20 + idx + 1}
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border small">
                            {v.epic_no}
                          </span>
                        </td>
                        <td>
                          <div className="fw-semibold small">{fullName(v)}</div>
                          {knName(v) && (
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              {knName(v)}
                            </div>
                          )}
                        </td>
                        <td className="small">
                          {genderLabel(v.gender)} · {v.age || "—"}
                        </td>
                        <td>
                          <span className="badge bg-secondary-subtle text-secondary small">
                            Booth {v.booth_no}
                          </span>
                        </td>
                        {tab !== "removed" && (
                          <td className="small">
                            {v.mobile ? (
                              <span>
                                <i className="bi bi-telephone text-success me-1"></i>
                                {v.mobile}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        )}
                        {tab === "edit" && (
                          <td className="small">
                            {v.casteid && casteMap[v.casteid] ? (
                              <span className="badge bg-primary-subtle text-primary">
                                {casteMap[v.casteid]}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        )}
                        {tab === "removed" && (
                          <>
                            <td>
                              <span
                                className={`badge small ${REASON_BADGE[v.delete_reason] || REASON_BADGE.Other}`}
                              >
                                {v.delete_reason || "—"}
                              </span>
                            </td>
                            <td className="small text-muted">
                              {v.deleted_at || "—"}
                            </td>
                            <td className="small text-muted">
                              {v.deleted_by || "—"}
                            </td>
                          </>
                        )}
                        {tab === "edit" && (
                          <td className="text-end pe-4">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openEdit(v)}
                            >
                              <i className="bi bi-pencil me-1"></i>Edit
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <Pager
            page={page}
            totalPages={totalPages}
            total={total}
            onPage={(p) => fetchRows(p)}
          />
        </div>
      )}

      {/* ── DUPLICATES TAB ── */}
      {tab === "duplicates" && (
        <div>
          <div className="card border-0 shadow-sm rounded-4 mb-3">
            <div className="card-body p-3 d-flex flex-wrap gap-3 align-items-center justify-content-between">
              <div className="btn-group">
                <button
                  type="button"
                  className={`btn ${dupType === "epic" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    setDupType("epic");
                    setSel({});
                    fetchDups(1, "epic");
                  }}
                >
                  Same EPIC number
                </button>
                <button
                  type="button"
                  className={`btn ${dupType === "name" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => {
                    setDupType("name");
                    setSel({});
                    fetchDups(1, "name");
                  }}
                >
                  Same name + relative + age + booth
                </button>
              </div>
              {dupData && (
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="badge bg-warning-subtle text-warning px-3 py-2">
                    {dupData.summary.groups} group(s)
                  </span>
                  <span className="badge bg-danger-subtle text-danger px-3 py-2">
                    {dupData.summary.extra_records} extra record(s)
                  </span>
                  {dupData.groups.length > 0 && (
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={selectExtras}
                    >
                      Select extras on this page
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {dupType === "name" && (
            <div className="alert alert-warning py-2 small">
              <i className="bi bi-exclamation-triangle me-1"></i>
              Same name, relative, age and booth is only a{" "}
              <strong>suspicion</strong>. Compare House No and Serial No before
              removing — two different people can match. Selection is cleared
              when you change page.
            </div>
          )}

          <div className="card border-0 shadow-sm rounded-4">
            {renderActionBar()}
            <div className="card-body p-3">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : !dupData || dupData.groups.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-check-circle fs-1 d-block mb-2 text-success"></i>
                  No duplicates found for this check
                </div>
              ) : (
                dupData.groups.map((g) => (
                  <div
                    key={g.key}
                    className="border rounded-3 mb-3 overflow-hidden"
                  >
                    <div
                      className="px-3 py-2 d-flex justify-content-between align-items-center"
                      style={{ background: "#f8fafc" }}
                    >
                      <span className="fw-semibold small">{g.key}</span>
                      <span className="badge bg-warning-subtle text-warning">
                        {g.count} records
                      </span>
                    </div>
                    <div className="table-responsive">
                      <table
                        className="table table-sm align-middle mb-0"
                        style={{ fontSize: "12px" }}
                      >
                        <thead>
                          <tr className="text-muted">
                            <th style={{ width: 36 }}></th>
                            <th>Serial</th>
                            <th>House</th>
                            <th>EPIC</th>
                            <th>Name</th>
                            <th>Relative</th>
                            <th>Age</th>
                            <th>Mobile</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.voters.map((v) => (
                            <tr
                              key={v.voter_id}
                              className={sel[v.voter_id] ? "table-danger" : ""}
                            >
                              <td className="ps-3">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={!!sel[v.voter_id]}
                                  onChange={() => toggleOne(v)}
                                />
                              </td>
                              <td>{v.serial_no ?? "—"}</td>
                              <td>{v.house_no || "—"}</td>
                              <td>
                                <span className="badge bg-light text-dark border">
                                  {v.epic_no}
                                </span>
                              </td>
                              <td>
                                <div className="fw-semibold">{fullName(v)}</div>
                                {knName(v) && (
                                  <div className="text-muted">{knName(v)}</div>
                                )}
                              </td>
                              <td>{relName(v) || "—"}</td>
                              <td>{v.age || "—"}</td>
                              <td>{v.mobile || "—"}</td>
                              <td>
                                {v.voter_id === g.suggested_keep && (
                                  <span className="badge bg-success-subtle text-success">
                                    Suggested keep
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </div>
            {dupData && (
              <Pager
                page={dupPage}
                totalPages={dupData.pagination.total_pages}
                total={dupData.pagination.total}
                onPage={(p) => {
                  setSel({});
                  fetchDups(p);
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* ── CONFIRM MODAL ── */}
      {confirmMode && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold">
                  {confirmMode === "remove"
                    ? "Confirm removal"
                    : "Confirm restore"}
                </h5>
                <button
                  className="btn-close"
                  onClick={() => setConfirmMode(null)}
                ></button>
              </div>
              <div className="modal-body px-4 py-3">
                {confirmMode === "remove" ? (
                  <p className="mb-2">
                    Mark <strong>{selCount}</strong> voter(s) as{" "}
                    <strong>{reasonToUse}</strong>? They will be hidden from
                    every list and search. You can restore them later from the
                    Removed List.
                  </p>
                ) : (
                  <p className="mb-2">
                    Restore <strong>{selCount}</strong> voter(s)? They will
                    appear in all lists again.
                  </p>
                )}
                <ul className="small text-muted mb-0">
                  {Object.values(sel)
                    .slice(0, 5)
                    .map((v) => (
                      <li key={v.voter_id}>
                        {fullName(v)} · {v.epic_no}
                      </li>
                    ))}
                  {selCount > 5 && <li>…and {selCount - 5} more</li>}
                </ul>
                {confirmMode === "remove" && fullGroups > 0 && (
                  <div className="alert alert-danger py-2 small mt-3 mb-0">
                    {fullGroups} group(s) have every copy selected. Untick at
                    least one copy in each group so one record stays.
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button
                  className="btn btn-light px-4"
                  onClick={() => setConfirmMode(null)}
                >
                  Cancel
                </button>
                <button
                  className={`btn px-4 text-white ${confirmMode === "remove" ? "btn-danger" : "btn-success"}`}
                  onClick={doConfirm}
                  disabled={
                    working || (confirmMode === "remove" && fullGroups > 0)
                  }
                >
                  {working ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : confirmMode === "remove" ? (
                    "Yes, remove"
                  ) : (
                    "Yes, restore"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL (mobile + caste only) ── */}
      {editVoter && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="modal-title fw-bold">Edit voter</h5>
                <button
                  className="btn-close"
                  onClick={() => setEditVoter(null)}
                ></button>
              </div>
              <div className="modal-body px-4 py-3">
                <div
                  className="p-3 rounded-3 mb-3"
                  style={{ background: "#f8fafc" }}
                >
                  <div className="fw-semibold">{fullName(editVoter)}</div>
                  {knName(editVoter) && (
                    <div className="text-muted small">{knName(editVoter)}</div>
                  )}
                  <div className="small text-muted mt-1">
                    {editVoter.epic_no} · {genderLabel(editVoter.gender)} · Age{" "}
                    {editVoter.age || "—"} · Booth {editVoter.booth_no}
                  </div>
                </div>

                {editError && (
                  <div className="alert alert-danger py-2 small">
                    {editError}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label small fw-medium">Mobile</label>
                  <input
                    type="text"
                    className="form-control"
                    maxLength={10}
                    placeholder="10 digit mobile"
                    value={editMobile}
                    onChange={(e) =>
                      setEditMobile(e.target.value.replace(/\D/g, ""))
                    }
                  />
                </div>
                <div className="mb-1">
                  <label className="form-label small fw-medium">Caste</label>
                  <select
                    className="form-select"
                    value={editCaste}
                    onChange={(e) => setEditCaste(e.target.value)}
                  >
                    <option value="">— None —</option>
                    {casteList.map((c) => (
                      <option key={c.casteid} value={c.casteid}>
                        {c.castename}
                      </option>
                    ))}
                  </select>
                  {casteList.length === 0 && (
                    <div className="form-text text-warning">
                      No castes yet — add them in Masters → Others → Caste.
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button
                  className="btn btn-light px-4"
                  onClick={() => setEditVoter(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn text-white px-4"
                  style={{ background: GRADIENT }}
                  onClick={saveEdit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoterCleanup;
